<script setup>
// Pareamento do aparelho (nexus-wa) de uma caixa: QR que se renova e, sem
// câmera, código para digitar no celular. Relê o estado a cada 3s enquanto
// estiver na tela e avisa quando conectar. Usado ao criar a caixa não oficial
// (Configurações → Caixas de entrada → WhatsApp) e na aba do aparelho da caixa.
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { createNexusApi, erroDaApi } from '../nexus/api';
import messages from '../nexus/i18n';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
  // { id, nome, telefone }
  caixa: { type: Object, required: true },
  fechavel: { type: Boolean, default: true },
});
const emit = defineEmits(['conectado', 'fechar', 'atualizou']);

const { t } = useI18n({ messages });
const api = computed(() => createNexusApi(props.accountId));

const atual = ref({ ...props.caixa, qr: null });
const codigo = ref('');
const gerandoCodigo = ref(false);
let relogio = null;

const estado = computed(() => atual.value.sessao?.estado || 'aguardando');
const esperandoQr = computed(() =>
  ['aguardando', 'qr', 'pareada'].includes(estado.value)
);

const rele = async () => {
  try {
    const { data } = await api.value.whatsappCaixa(props.caixa.id);
    atual.value = data;
    emit('atualizou', data);
    if (data.sessao?.conectada) {
      clearInterval(relogio);
      useAlert(t('NEUXS_NEXUS.WHATSAPP.CONECTADO'));
      emit('conectado', data);
    }
  } catch {
    // Falha passageira: a próxima volta tenta de novo.
  }
};

onMounted(() => {
  rele();
  relogio = setInterval(rele, 3000);
});
onBeforeUnmount(() => clearInterval(relogio));

const geraCodigo = async () => {
  gerandoCodigo.value = true;
  try {
    const { data } = await api.value.codigoWhatsapp(props.caixa.id);
    codigo.value = data.codigo;
  } catch (error) {
    useAlert(erroDaApi(error, t('NEUXS_NEXUS.WHATSAPP.ERRO_ACAO')));
  } finally {
    gerandoCodigo.value = false;
  }
};
</script>

<template>
  <section
    class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
  >
    <div class="flex items-center justify-between gap-2">
      <span class="font-medium text-n-slate-12">
        {{ t('NEUXS_NEXUS.WHATSAPP.PAREAMENTO', { nome: caixa.nome }) }}
      </span>
      <Button
        v-if="fechavel"
        :label="t('NEUXS_NEXUS.WHATSAPP.FECHAR')"
        size="sm"
        variant="ghost"
        color="slate"
        @click="emit('fechar')"
      />
    </div>
    <p class="text-sm text-n-slate-11">
      {{ t('NEUXS_NEXUS.WHATSAPP.PASSOS', { telefone: caixa.telefone }) }}
    </p>
    <div class="flex flex-col items-center gap-2">
      <img
        v-if="atual.qr"
        :src="`data:image/png;base64,${atual.qr}`"
        alt="QR"
        class="bg-white rounded-lg size-64"
      />
      <div
        v-else-if="esperandoQr"
        class="flex flex-col items-center justify-center gap-2 size-64"
      >
        <Spinner />
        <span class="text-sm text-n-slate-11">
          {{ t('NEUXS_NEXUS.WHATSAPP.GERANDO_QR') }}
        </span>
      </div>
      <p v-else class="text-sm text-n-slate-11">
        {{ t('NEUXS_NEXUS.WHATSAPP.SEM_QR') }}
      </p>
      <span v-if="atual.qr" class="text-xs text-n-slate-10">
        {{ t('NEUXS_NEXUS.WHATSAPP.QR_RENOVA') }}
      </span>
    </div>
    <div class="flex flex-col gap-2">
      <Button
        v-if="!codigo"
        :label="t('NEUXS_NEXUS.WHATSAPP.CODIGO')"
        size="sm"
        variant="link"
        :is-loading="gerandoCodigo"
        @click="geraCodigo"
      />
      <template v-else>
        <span class="text-sm text-n-slate-11">
          {{ t('NEUXS_NEXUS.WHATSAPP.CODIGO_PASSOS') }}
        </span>
        <code
          class="self-start px-3 py-2 text-lg tracking-widest rounded-lg bg-n-alpha-2"
        >
          {{ codigo }}
        </code>
      </template>
    </div>
  </section>
</template>
